import { Button, cn } from "@beweegbaas/ui";
import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import { motion, type HTMLMotionProps } from "motion/react";
import type { ComponentProps } from "react";
import { useMemo } from "react";
import { useSpinDelay } from "spin-delay";
import { tv } from "tailwind-variants";
import { useLeerlingGestuurdeLes } from "#/modules/les.leerling-gestuurd/use-leerling-gestuurde-les";
import type { components } from "#/schema";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/student/",
)({
  component: StudentIndexPage,
});

type StudentLessonParams = {
  locale: string;
  schoolyear?: string;
  school: string;
  lesson: string;
};

interface StudentProps extends Omit<ComponentProps<typeof Button>, "render" | "nativeButton"> {
  leerling: components["schemas"]["LesgroepLeerlingModel"];
  params: StudentLessonParams;
  groupIndex: number;
}

// Render a single student
function Student({ leerling, params, className, groupIndex: _groupIndex, ...props }: StudentProps) {
  const { getStudentLinkOptions, layoutType, lesson } = useLeerlingGestuurdeLes();
  const longName = leerling.name.length > 10;
  const showAnimal = lesson.avatarsEnabled;
  const navigation = useRouterState({
    select: (state) => ({
      status: state.status,
      pathname: state.location.pathname,
    }),
  });

  const studentIsPending =
    navigation.status === "pending" && navigation.pathname.includes(leerling.id);
  const isPending = useSpinDelay(studentIsPending);

  return (
    <Button
      variant="reset"
      key={leerling.id}
      className={cn(
        "items-start justify-start text-left",
        `break-words inline-flex items-center justify-center gap-2 text-balance
        hyphens-auto no-underline`,
        "font-heading font-black text-blue-800",
        "size-full max-h-[120px] min-h-[80px] px-5 py-0 leading-[1.1]",
        "rounded-full",
        "clicky-button-shadow",
        `bg-linear-to-b from-white/80 to-white/60 hover:from-white/90
        hover:to-white/70`,
        "relative",
        "overflow-hidden text-inherit",
        { "text-[clamp(.9rem,1.3vw,1.1rem)]!": longName },
        { "max-2xl:min-h-[35px]": layoutType === "small" },
        { "max-2xl:min-h-[60px]": layoutType === "medium" },
        { "max-2xl:min-h-[90px]": layoutType === "large" },
        {
          [`pr-[calc(clamp(40px,8vw,80px)+1rem)] max-lg:flex-col
          lg:text-[clamp(1rem,1.2vw,1.3rem)]`]: showAnimal,
        },
        {
          "max-2xl:pr-[calc(clamp(30px,8vw,50px)+1rem)]": layoutType === "small" && showAnimal,
        },
        {
          "max-2xl:pr-[calc(clamp(40px,8vw,80px)+1rem)]": layoutType === "medium" && showAnimal,
        },
        {
          "max-2xl:pr-[calc(clamp(40px,8vw,80px)+1rem)]": layoutType === "large" && showAnimal,
        },
        { "text-[clamp(.9rem,1.6vw,1.3rem)]!": !showAnimal },
        className,
      )}
      disabled={isPending}
      render={<Link {...getStudentLinkOptions({ ...params, studentId: leerling.id })} />}
      {...props}
    >
      {leerling.name}
      {showAnimal && (
        <>
          <img
            alt=""
            src={`/avatars/animals-5/${leerling.avatarNummer}.svg`}
            className={cn(
              `absolute right-3 bottom-1/2 h-auto max-h-full max-w-[clamp(40px,8vw,75px)] shrink-0
              translate-y-1/2 object-contain opacity-50 blur
              brightness-50`,
              {
                "max-2xl:max-w-[clamp(30px,6vw,50px)]": layoutType === "small",
                "max-2xl:max-w-[clamp(40px,8vw,75px)]": layoutType === "medium",
                "max-2xl:max-w-[clamp(50px,10vw,100px)]": layoutType === "large",
              },
            )}
          />
          <img
            alt=""
            src={`/avatars/animals-5/${leerling.avatarNummer}.svg`}
            className={cn(
              `absolute right-3 bottom-1/2 h-auto max-h-full max-w-[clamp(40px,8vw,75px)]
              shrink-0 translate-y-1/2 object-contain`,
              {
                "max-2xl:max-w-[clamp(30px,6vw,50px)]": layoutType === "small",
                "max-2xl:max-w-[clamp(40px,8vw,75px)]": layoutType === "medium",
                "max-2xl:max-w-[clamp(50px,10vw,100px)]": layoutType === "large",
              },
            )}
          />
        </>
      )}
    </Button>
  );
}

interface LesGroepProps extends HTMLMotionProps<"article"> {
  lesgroep: components["schemas"]["LesgroepModel"];
  params: StudentLessonParams;
  index: number;
}

// LesGroep variants using the base color system
const LesGroepVariants = tv({
  base: ["rounded-lg p-6"],
  variants: {
    color: {
      blue: "bg-gradient-to-br text-blue-900 bg-linear-to-tl to-blue-500 from-blue-400",
      green: "bg-gradient-to-br text-green-900 bg-linear-to-tl to-green-500 from-green-400",
      red: "bg-gradient-to-br text-red-900 bg-linear-to-tl to-red-500 from-red-400",
      yellow: "bg-gradient-to-br text-yellow-900 bg-linear-to-tl to-yellow-500 from-yellow-400",
      purple: "bg-gradient-to-br text-purple-900 bg-linear-to-tl to-purple-500 from-purple-400",
      orange: "bg-gradient-to-br text-orange-900 bg-linear-to-tl to-orange-500 from-orange-400",
      pink: "bg-gradient-to-br text-pink-900 bg-linear-to-tl to-pink-500 from-pink-400",
      amber: "bg-gradient-to-br text-amber-900 bg-linear-to-tl to-amber-500 from-amber-400",
      rose: "bg-gradient-to-br text-rose-900 bg-linear-to-tl to-rose-500 from-rose-400",
      violet: "bg-gradient-to-br text-violet-900 bg-linear-to-tl to-violet-500 from-violet-400",
      teal: "bg-gradient-to-br text-teal-900 bg-linear-to-tl to-teal-500 from-teal-400",
      gray: "bg-gradient-to-br text-gray-900 bg-linear-to-tl to-gray-500 from-gray-400",
    },
  },
  defaultVariants: {
    color: "blue",
  },
});

// Loop over all leerlingen in a lesgroep
function LesGroep({ lesgroep, params, className, index, style: _style, ...props }: LesGroepProps) {
  const { amountOfGroups, layoutType, getLesgroepColor } = useLeerlingGestuurdeLes();

  const color = useMemo(() => {
    return getLesgroepColor(index);
  }, [index, getLesgroepColor]);

  return (
    <motion.article
      className={cn(LesGroepVariants({ color }), "relative flex grow", className)}
      {...props}
    >
      <span
        className="font-heading absolute bottom-2 left-2 text-base leading-none
          font-black opacity-50"
      >
        {lesgroep.groep}
      </span>
      <div
        className={cn(
          "relative grid grow gap-6 p-10",
          {
            "max-2xl:gap-4 max-2xl:p-5 max-xl:gap-2 max-xl:p-3": layoutType === "medium",
          },
          {
            "max-2xl:gap-3 max-2xl:p-4 max-xl:gap-1 max-xl:p-1": layoutType === "small",
          },
          { "gap-6 p-10": layoutType === "large" },
          { "grid-auto-fill": lesgroep.leerlingen.length < 4 },
          {
            "grid-auto-fit max-lg:[--auto-fit-size:clamp(170px,10vw,300px)]":
              lesgroep.leerlingen.length >= 4,
          },
          {
            [`xl:place-content-center xl:px-[15vw]
            2xl:[--auto-fit-size:clamp(250px,10vw,300px)]`]: amountOfGroups === 1,
          },
        )}
      >
        {lesgroep.leerlingen.map((leerling: components["schemas"]["LesgroepLeerlingModel"]) => (
          <Student key={leerling.id} leerling={leerling} params={params} groupIndex={index} />
        ))}
      </div>
    </motion.article>
  );
}

function StudentIndexPage() {
  const params = Route.useParams();
  const { lesson } = useLeerlingGestuurdeLes();

  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.05 },
    },
  };

  const item = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1 },
  };

  return (
    <motion.div
      style={{ fontSize: "clamp(1.3rem, 1.7vw, 2rem)" }}
      className={cn("grid grow gap-2 p-2", {
        "grid-cols-1": lesson.lesgroepen.length === 1,
        "grid-cols-2": lesson.lesgroepen.length === 2,
        "flex flex-wrap": lesson.lesgroepen.length === 3,
        "grid-cols-2 ": lesson.lesgroepen.length === 4,
        "flex flex-wrap *:basis-[clamp(400px,10vw,600px)]": lesson.lesgroepen.length > 4,
      })}
      variants={container}
      initial="hidden"
      animate="show"
    >
      {lesson.lesgroepen.map((lesgroep, index) => (
        <LesGroep
          key={lesgroep.groep}
          className="grow basis-[600px]"
          lesgroep={lesgroep}
          params={params}
          index={index}
          variants={item}
        />
      ))}
    </motion.div>
  );
}
